import React from 'react';
import { Tag, Checkbox } from 'antd';
import { RightOutlined, SearchOutlined } from '@ant-design/icons';
import { Input, Segmented, Button, Divider } from 'antd';
import {
    BCCSelectionType,
    BCCSelectionLevel,
    BCCSelectionMode,
    IBCCSelectionTagProps,
    IBCCSelectableItemProps,
    IBCCSelectAllItemProps,
    IBCCZoneItemProps,
    IBCCBreadcrumbProps,
    IBCCSearchBarProps,
    IBCCLevelSelectorProps,
    IBCCModeToggleProps,
    IBCCCohortLevelSelectorProps,
    IBCCCohortPreviewProps,
    IBCCFooterProps,
} from '@ts/common-types';
import { BCC_LEVEL_DISPLAY_NAMES, BCC_SELECTION_TAG_COLORS, COHORT_LEVEL_OPTIONS, SELECTION_LEVELS, CITY_SELECTION_MODE_LABELS } from '@ts/constants';
import { getSelectionTooltip } from '@ts/bcc-city-selection-utils';

export const SelectionTag: React.FC<IBCCSelectionTagProps> = ({
    selection,
    closable = true,
    onRemove,
    citiesList,
}) => {
    const tooltipContent = getSelectionTooltip(selection, citiesList);
    const tagColor =
        selection.type === BCCSelectionType.COHORT
            ? BCC_SELECTION_TAG_COLORS.cohort
            : BCC_SELECTION_TAG_COLORS.default;

    return (
        <Tag
            key={selection.id}
            closable={closable}
            onClose={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onRemove(selection.id);
            }}
            color={tagColor}
            className="city-selection-dropdown__tag"
            title={tooltipContent || undefined}
        >
            <span className="city-selection-dropdown__tag-label">
                {selection.label}
            </span>
        </Tag>
    );
};

export const SelectableItem: React.FC<IBCCSelectableItemProps> = ({
    id,
    label,
    selected,
    disabled,
    disabledReason,
    onToggle,
}) => (
    <div
        key={id}
        className={`city-selection-dropdown__item ${selected ? 'city-selection-dropdown__item--selected' : ''} ${disabled ? 'city-selection-dropdown__item--disabled' : ''}`}
    >
        <Checkbox
            checked={selected || disabled}
            disabled={disabled}
            onChange={() => !disabled && onToggle()}
        />
        <span
            className={`city-selection-dropdown__item-label ${disabled ? 'city-selection-dropdown__item-label--disabled' : ''}`}
            onClick={() => !disabled && onToggle()}
        >
            {label}
        </span>
        {disabled && disabledReason && (
            <span className="city-selection-dropdown__item-badge">
                {disabledReason}
            </span>
        )}
    </div>
);

export const SelectAllItem: React.FC<IBCCSelectAllItemProps> = ({
    count,
    allSelected,
    someSelected,
    onToggle,
    className = '',
}) => (
    <div
        className={`city-selection-dropdown__item city-selection-dropdown__item--select-all ${allSelected ? 'city-selection-dropdown__item--selected' : ''} ${className}`}
    >
        <Checkbox
            checked={allSelected}
            indeterminate={someSelected && !allSelected}
            onChange={onToggle}
        />
        <span
            className="city-selection-dropdown__item-label city-selection-dropdown__item-label--select-all"
            onClick={onToggle}
        >
            Select All ({count})
        </span>
    </div>
);

export const ZoneItem: React.FC<IBCCZoneItemProps> = ({
    zoneName,
    selected,
    disabled,
    onToggle,
    onExpand,
}) => (
    <div
        className={`city-selection-dropdown__zone-item ${selected ? 'city-selection-dropdown__zone-item--selected' : ''}`}
    >
        <Checkbox
            checked={selected || disabled}
            disabled={disabled}
            onChange={(e) => {
                e.stopPropagation();
            }}
            onClick={(e) => {
                e.stopPropagation();
                if (!disabled) {
                    onToggle(e);
                }
            }}
        />
        <div
            className="city-selection-dropdown__zone-item-content"
            onClick={onExpand}
        >
            <span
                className={`city-selection-dropdown__zone-item-label ${disabled ? 'city-selection-dropdown__zone-item-label--disabled' : ''}`}
            >
                {zoneName}
            </span>
            <RightOutlined className="city-selection-dropdown__zone-item-expand" />
        </div>
    </div>
);

export const Breadcrumb: React.FC<IBCCBreadcrumbProps> = ({
    currentZone,
    onBack,
}) => (
    <div className="city-selection-dropdown__breadcrumb">
        <span
            className="city-selection-dropdown__breadcrumb-link"
            onClick={onBack}
        >
            Custom
        </span>
        <RightOutlined className="city-selection-dropdown__breadcrumb-separator" />
        <span className="city-selection-dropdown__breadcrumb-current">
            {currentZone}
        </span>
    </div>
);

export const SearchBar: React.FC<IBCCSearchBarProps> = ({
    placeholder,
    value,
    onChange,
}) => (
    <div className="city-selection-dropdown__search">
        <Input
            placeholder={placeholder}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            prefix={<SearchOutlined className="city-selection-dropdown__search-icon" />}
            allowClear
            className="city-selection-dropdown__search-input"
        />
    </div>
);

export const LevelSelector: React.FC<IBCCLevelSelectorProps> = ({
    currentLevel,
    onLevelChange,
}) => (
    <div className="city-selection-dropdown__level-column">
        {SELECTION_LEVELS.map((level) => (
            <div
                key={level}
                onClick={() => onLevelChange(level)}
                className={`city-selection-dropdown__level-item ${currentLevel === level ? 'city-selection-dropdown__level-item--active' : ''}`}
            >
                <span>{BCC_LEVEL_DISPLAY_NAMES[level]}</span>
                <RightOutlined className="city-selection-dropdown__level-arrow" />
            </div>
        ))}
    </div>
);

export const ModeToggle: React.FC<IBCCModeToggleProps> = ({
    currentMode,
    onModeChange,
}) => (
    <div className="city-selection-dropdown__mode-toggle">
        <div className="city-selection-dropdown__mode-toggle-container">
            {([BCCSelectionMode.INDIVIDUAL, BCCSelectionMode.COHORT] as const).map(
                (m) => (
                    <Button
                        key={m}
                        onClick={() => onModeChange(m)}
                        className={`city-selection-dropdown__mode-btn ${
                            currentMode === m
                                ? 'city-selection-dropdown__mode-btn--active'
                                : ''
                        } ${
                            m === BCCSelectionMode.COHORT && currentMode === m
                                ? 'city-selection-dropdown__mode-btn--cohort-active'
                                : ''
                        }`}
                        style={
                            currentMode === m
                                ? { boxShadow: 'none' }
                                : { backgroundColor: 'transparent', boxShadow: 'none' }
                        }
                    >
                        {CITY_SELECTION_MODE_LABELS[m]}
                    </Button>
                )
            )}
        </div>
    </div>
);

export const CohortLevelSelector: React.FC<IBCCCohortLevelSelectorProps> = ({
    selectedLevel,
    onLevelChange,
}) => (
    <div className="city-selection-dropdown__cohort-level-selector">
        <div className="city-selection-dropdown__cohort-level-label">
            Select Level
        </div>
        <Segmented
            block
            value={selectedLevel}
            onChange={(value) => onLevelChange(value as BCCSelectionLevel)}
            options={COHORT_LEVEL_OPTIONS}
        />
    </div>
);

export const CohortPreview: React.FC<IBCCCohortPreviewProps> = ({
    items,
    onRemove,
    getCohortItemLabel,
}) => {
    if (items.length === 0) return null;

    return (
        <div className="city-selection-dropdown__cohort-preview">
            <div className="city-selection-dropdown__cohort-preview-content">
                {items.map((item, idx) => (
                    <React.Fragment key={item}>
                        <Tag
                            closable
                            onClose={() => onRemove(item)}
                            color="blue"
                            style={{ margin: 0 }}
                        >
                            {getCohortItemLabel(item)}
                        </Tag>
                        {idx < items.length - 1 && (
                            <span className="city-selection-dropdown__cohort-preview-separator">
                                +
                            </span>
                        )}
                    </React.Fragment>
                ))}
            </div>
        </div>
    );
};

export const Footer: React.FC<IBCCFooterProps> = ({
    selections,
    onClearAll,
    onApply,
    renderTag,
}) => {
    if (selections.length === 0) return null;

    return (
        <div className="city-selection-dropdown__footer">
            <div className="city-selection-dropdown__footer-header">
                <span className="city-selection-dropdown__footer-label">
                    Current Selections ({selections.length})
                </span>
                <Button
                    type="link"
                    onClick={onClearAll}
                    className="city-selection-dropdown__footer-clear-btn"
                >
                    Clear all
                </Button>
            </div>
            <div
                className="city-selection-dropdown__footer-tags"
                style={{ maxHeight: '120px', overflowY: 'auto' }}
            >
                {selections.map((sel) => renderTag(sel))}
            </div>
            <Divider className="my-2" />
            <div className="d-flex justify-content-end">
                <Button type="primary" onClick={onApply}>
                    Apply
                </Button>
            </div>
        </div>
    );
};

